import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type MouseEvent } from 'react';

import type { ParticipantId, GroupId, WallParticipant, WallGroup } from '@walls/store';

interface GroupsListProps {
  canManageGroups: boolean;
  participants: WallParticipant[];
  groups: WallGroup[];
  visibleParticipantIds: ParticipantId[];
  onDeleteGroup: (groupId: GroupId) => void;
  onEditGroup: (group: WallGroup) => void;
  onToggleVisibleParticipants: (participantIds: ParticipantId[]) => void;
}

export function GroupsList({
  canManageGroups,
  participants,
  groups,
  visibleParticipantIds,
  onDeleteGroup,
  onEditGroup,
  onToggleVisibleParticipants,
}: GroupsListProps) {
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<HTMLElement | null>(null);
  const [groupMenuId, setGroupMenuId] = useState<GroupId | null>(null);
  const participantNameById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant.name])),
    [participants]
  );

  const openGroupMenu = (event: MouseEvent<HTMLButtonElement>, groupId: GroupId) => {
    setGroupMenuAnchor(event.currentTarget);
    setGroupMenuId(groupId);
  };

  const closeGroupMenu = () => {
    setGroupMenuAnchor(null);
    setGroupMenuId(null);
  };

  return (
    <Stack spacing={1.25} sx={{ p: 1.5 }}>
      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 1 }}>
          No groups yet
        </Typography>
      ) : (
        groups.map((group) => {
          const isGroupSelected =
            group.participantIds.length > 0 &&
            group.participantIds.every((participantId) =>
              visibleParticipantIds.includes(participantId)
            );
          const displayedNames = group.participantIds
            .map((participantId) => participantNameById.get(participantId))
            .filter((name): name is string => Boolean(name));

          return (
            <Box
              key={group.id}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={1} sx={{ p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" noWrap sx={{ flex: '1 1 auto' }}>
                    {group.name}
                  </Typography>
                  <Tooltip title={isGroupSelected ? 'Hide group from focus' : 'Show group in focus'}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={isGroupSelected ? `Hide ${group.name}` : `Show ${group.name}`}
                        onClick={() => onToggleVisibleParticipants(group.participantIds)}
                        disabled={group.participantIds.length === 0}
                        sx={{
                          bgcolor: isGroupSelected ? '#0f2a39' : 'transparent',
                          color: isGroupSelected ? '#fff' : 'text.secondary',
                          flex: '0 0 auto',
                          '&:hover': {
                            bgcolor: isGroupSelected ? '#16384b' : '#f3f4f6',
                          },
                        }}
                      >
                        {isGroupSelected ? (
                          <VisibilityIcon fontSize="small" />
                        ) : (
                          <VisibilityOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canManageGroups && (
                    <>
                      <Tooltip title="Group actions">
                        <IconButton
                          size="small"
                          aria-label={`${group.name} actions`}
                          aria-controls={groupMenuId === group.id ? 'group-actions-menu' : undefined}
                          aria-expanded={groupMenuId === group.id ? true : undefined}
                          aria-haspopup="menu"
                          onClick={(event) => openGroupMenu(event, group.id)}
                          sx={{ color: 'text.secondary', flex: '0 0 auto' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="group-actions-menu"
                        anchorEl={groupMenuAnchor}
                        open={groupMenuId === group.id}
                        onClose={closeGroupMenu}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      >
                        <MenuItem
                          onClick={() => {
                            closeGroupMenu();
                            onEditGroup(group);
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          Edit group
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            closeGroupMenu();
                            onDeleteGroup(group.id);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                            <DeleteIcon fontSize="small" />
                          </ListItemIcon>
                          Delete group
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Stack>
                {displayedNames.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {displayedNames.map((name) => (
                      <Chip key={name} label={name} size="small" />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No participants assigned
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })
      )}
    </Stack>
  );
}
